import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import { useParams } from 'react-router-dom';
import {
  faCheck,
  faChevronDown,
  faChevronRight,
  faClose
} from '@fortawesome/free-solid-svg-icons';
import { useGetViewStandardMutation } from '../../../../../../../features/authentication/reportApiSlice';
import Modals from './Modals';
import { capitalizeFirstLetter, mergeObjectsByNumber } from '../../../../../utils';
import QuestionPreviewModal from '../../../../../components/QuestionPreviewModal';

const Table = ({
  tittle,
  expand,
  setExpand,
  details,
  standard,
  selectedCreditId,
  sessionReportOptions,
  setUpdatedData,
  initialData,
  titleName,
  cumilativeReport,
  mcStyleAlphabet,
  hideRetiesColumn,
  isAllReport
}) => {
  const [viewStandardDetails, setViewStandardDetails] = React.useState({});
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [getViewStandard] = useGetViewStandardMutation();
  const [showAll, setShowAll] = React.useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [showQuestionPreviewModal, setShowQuestionPreviewModal] = React.useState(false);
  const [isHoverIndex, setIsHoverIndex] = React.useState(null);
  const { Id } = useParams();
  let timeoutId;
  const [questionId, setQuestionId] = React.useState();

  const handleMouseEnter = (index) => {
    setIsHoverIndex(index);
    setIsHovering(true);
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => setShowAll(true), 300); // Set the delay here (300ms in this example)
  };

  const handleMouseLeave = (index) => {
    setIsHoverIndex(index);
    setIsHovering(false);
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => setShowAll(false), 300); // Set the delay here (300ms in this example)
  };

  const fetchViewStandard = async (value) => {
    if (!value.standardNumber) {
      return;
    }

    let option = {
      stateCourseId: parseInt(value.stateCourseId),
      standardNumber: String(value.standardNumber),
      stateId: value.stateId
    };

    try {
      const { data } = await getViewStandard(option);
      if (data) {
        setViewStandardDetails(data);
        setIsModalOpen(true);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const updatedTableDetails = React.useMemo(() => {
    if(sessionReportOptions.renderOnly){
      return details;
    }
    else{
      const data = mergeObjectsByNumber(details, selectedCreditId, titleName, sessionReportOptions, isAllReport);
      setUpdatedData((prev) => {
        return {
          ...prev,
          [titleName]: data[titleName]
        };
      });
      return data[titleName];
    }
  }, [details, selectedCreditId, sessionReportOptions]);

  const questionDetailsModalHandler = async (questionId) => {   
    setQuestionId(questionId),
    setShowQuestionPreviewModal(!showQuestionPreviewModal);   
  };

  return (
    <div className="mt-5">
      <div className="d-flex justify-content-between">
        <p className="table_tittle">{tittle}</p>

        <button
          style={{
            background: '#e1eeff',
            color: 'rgba(84, 113, 200, 0.6)',
            width: '30px',
            height: '30px',
            textAlign: 'center',
            border: 'none',
            marginLeft: '10px'
          }}
          className="rounded-circle"
          onClick={() => setExpand(!expand)}>
          {expand ? (
            <FontAwesomeIcon icon={faChevronDown} />
          ) : (
            <FontAwesomeIcon icon={faChevronRight} />
          )}
        </button>
      </div>
      {expand && (
        <table className={'w-100'}>
          <tr className="table-head">
            <th>Totals</th>
            <th> Score</th>
            {sessionReportOptions?.correctIncorrectRetry && (
              <>
                <th>
                  <FontAwesomeIcon icon={faCheck} />
                </th>
                <th>
                  <FontAwesomeIcon icon={faClose} />
                </th>
                {!hideRetiesColumn && 
                <th style={{ width: 'auto' }}>
                  <div className="d-flex flex-shrink-0">
                    <FontAwesomeIcon icon={faClose} /> <FontAwesomeIcon icon={faCheck} />
                  </div>
                </th>}
                <th style={{ width: '9%' }}>CR Answered</th>
              </>
            )}
            {sessionReportOptions?.renderOnly && (
              <>
                <th>
                  <FontAwesomeIcon icon={faCheck} />
                </th>
                <th>
                  <FontAwesomeIcon icon={faClose} />
                </th>
              </>
            )}
            <th className="text-start">
              {titleName === 'questions' && sessionReportOptions?.answerDistribution
                ? 'Question(with answer distribution)'
                : `${capitalizeFirstLetter(titleName)}`}
            </th>
          </tr>

          {updatedTableDetails?.map((item, index) => {
            if (
              titleName !== 'questions' &&
              item.correct === 0 &&
              item.inCorrect === 0 &&
              item.correctOnRetry === 0 &&
              item.crAnswered === 0
            ) {
              return null; // Skip rendering this item
            }
            return (
              <tr key={'score-' + index} className="tabel-body">
                <td>
                  {typeof item.score === 'string' ? (
                    item.score
                  ) : (
                    <CircularProgressbar
                      value={Math.round(item.score)}
                      text={`${Math.round(item.score)}%`}
                      strokeWidth={12}
                      styles={buildStyles({
                        pathColor:
                          item.score >= sessionReportOptions.passingScore ? '#52C9A5' : '#fb5f5f',
                        textColor: '#525252',
                        trailColor: '#d6d6d6'
                      })}
                    />
                  )}
                </td>

                <td>{typeof item.score === 'number' ? Math.round(item.score) : item.score}</td>
                {sessionReportOptions?.correctIncorrectRetry && (
                  <>
                    <td>{item.correct}</td>
                    <td>{item.inCorrect}</td>
                    {!hideRetiesColumn && <td>{item.correctOnRetry}</td>}
                    <td>{item.crAnswered}</td>
                  </>
                )}
                {sessionReportOptions?.renderOnly && (
                  <>
                    <td>{item.correct}</td>
                    <td>{item.inCorrect}</td>
                  </>
                )}
                {standard ? (
                  <td
                    className={`${
                      item.standardNumber && 'text-decoration-underline cursor-pointer'
                    } text-start w-100 text-break`}
                    dangerouslySetInnerHTML={{
                      __html: `${item.description} ${
                        item.standardNumber !== null ? ': ' + item.standardNumber : ''
                      }`
                    }}
                    onClick={() => fetchViewStandard(item)}></td>
                ) : cumilativeReport ? (
                  <td id="question_data" className="text-start w-100">
                    <span
                      style={{ cursor: 'pointer' }}
                      onClick={() => questionDetailsModalHandler(item.questionId)}
                      className="text-decoration-underline me-3">
                      {index + 1}
                    </span>
                    {sessionReportOptions?.answerDistribution &&
                      (item.mcChoiceDistribution?.choices?.length > 0 ? (
                        item.mcChoiceDistribution?.choices?.map((value, index) => {
                          return (
                            <span
                              key={'standard-' + index}
                              className={`ms-3 ${
                                item?.mcChoiceDistribution?.correctAnswer === index + 1
                                  ? 'highlighted'
                                  : ''
                              }`}>
                              {mcStyleAlphabet ? String.fromCharCode(65 + index) : index + 1} :{' '}
                              {value}
                            </span>
                          );
                        })
                      ) : (
                        <span>{'<Fill-in or CR>'}</span>
                      ))}
                    {item.standards !== null && item.standards.length > 0 && (
                      <span
                        onClick={() => fetchViewStandard(item.standards[0])}
                        className={`ms-3 ${
                          item.standards[0].standardNumber !== null &&
                          'text-decoration-underline cursor-pointer'
                        }`}>
                        {' '}
                        {item.standards[0].description} : {item.standards[0].standardNumber}
                      </span>
                    )}
                    {item.standards !== null && item.standards.length > 1 && (
                      <>
                        <span
                          onMouseEnter={() => handleMouseEnter(index)}
                          onMouseLeave={() => handleMouseLeave(index)}
                          className="btn btn-link p-0">
                          See All
                        </span>

                        {isHovering && isHoverIndex === index && (
                          <div
                            onMouseEnter={() => handleMouseEnter(index)}
                            onMouseLeave={() => handleMouseLeave(index)}
                            className="tooltip-content">
                            {item.standards.slice(1).map((standard, index) => (
                              <span
                                onClick={() => fetchViewStandard(standard)}
                                key={index}
                                className="mt-2 text-decoration-underline cursor-pointer">
                                {standard.description} : {standard.standardNumber}
                              </span>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </td>
                ) : (
                  <td
                    style={{ width: '100%' }}
                    dangerouslySetInnerHTML={{ __html: item.description }}
                    className="text-start "></td>
                )}
              </tr>
            );
          })}
        </table>
      )}
      <Modals
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        details={viewStandardDetails}
      />

      <QuestionPreviewModal
        isOpen={showQuestionPreviewModal}
        onClose={() => setShowQuestionPreviewModal(false)}
        questionId={questionId}
        assignmentId={parseInt(Id)}
      />
    </div>
  );
};

export default Table;
